import type { AdminFamilyDto, AdminStatsDto, AdminUserDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { BarChart } from "../components/BarChart.tsx";
import { SkeletonCard } from "../components/EmptyState.tsx";
import { Segmented } from "../components/Segmented.tsx";
import { toast } from "../components/Toast.tsx";
import { compact, dayTitle, localDay } from "../format.ts";
import { t, tryT, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { confirmDialog, hapticTap } from "../telegram.ts";
import { useRemote, type RemoteState } from "../useRemote.ts";

type Tab = "overview" | "families" | "users";
type Metric = "activeUsers" | "requests" | "transactions" | "costUsd";

const ROLE_ICON = { admin: "👑", adult: "🧑", child: "🧒" } as const;

function usd(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return "<$0.01";
  return `$${n < 100 ? n.toFixed(2) : Math.round(n).toLocaleString("en-US")}`;
}

function ago(iso: string | null, lang: Lang): string {
  if (!iso) return t("neverSeen");
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return t("justNow");
  if (min < 60) return t("minAgo", { n: min });
  if (min < 24 * 60) return t("hoursAgo", { n: Math.floor(min / 60) });
  return dayTitle(localDay(iso), lang);
}

/** Панель разработчика: трафик, токены и их стоимость, семьи и пользователи, блокировка. */
export function AdminPage({ lang, onBack }: { lang: Lang; onBack: () => void }) {
  const [tab, setTab] = useState<Tab>("overview");
  return (
    <main className="dash">
      <div className="list-header">
        <button className="icon-btn back-btn" onClick={onBack} aria-label="back">
          ←
        </button>
        <h2 className="page-heading">🛠 {t("adminTitle")}</h2>
      </div>
      <Segmented
        value={tab}
        onChange={setTab}
        options={[
          { value: "overview", label: t("adminOverview") },
          { value: "families", label: t("adminFamilies") },
          { value: "users", label: t("adminUsers") },
        ]}
      />
      {tab === "overview" && <Overview lang={lang} />}
      {tab === "families" && <Families lang={lang} />}
      {tab === "users" && <Users lang={lang} />}
    </main>
  );
}

function Loading<T>({ remote }: { remote: RemoteState<T> }) {
  if (remote.loading)
    return (
      <>
        <SkeletonCard lines={2} />
        <SkeletonCard lines={4} />
      </>
    );
  return <p className="empty">{remote.error ? errorText(remote.error) : t("loadError")}</p>;
}

function Tile({ icon, label, value, sub }: { icon: string; label: string; value: string | number; sub: (string | null)[] }) {
  return (
    <div className="stat-tile">
      <div className="stat-label">
        {icon} {label}
      </div>
      <div className="stat-tile-value">{value}</div>
      {sub.filter(Boolean).map((s) => (
        <div key={s} className="stat-sub">
          {s}
        </div>
      ))}
    </div>
  );
}

function Overview({ lang }: { lang: Lang }) {
  const remote = useRemote("admin:stats", () => api.adminStats());
  const [metric, setMetric] = useState<Metric>("activeUsers");
  const s = remote.data;
  if (!s) return <Loading remote={remote} />;

  const labels = s.daily.map((d) => `${d.date.slice(8, 10)}.${d.date.slice(5, 7)}`);
  const values = s.daily.map((d) => (metric === "requests" ? d.botUpdates + d.apiRequests : d[metric]));
  const format = (v: number) => (metric === "costUsd" ? usd(v) : String(Math.round(v)));

  return (
    <>
      <div className="stat-grid">
        <Tile icon="👥" label={t("usersL")} value={s.users.total} sub={[t("newUsers7", { n: s.users.new7d }), s.users.blocked ? t("blockedN", { n: s.users.blocked }) : null]} />
        <Tile icon="🏠" label={t("familiesL")} value={s.families.total} sub={[t("active7Families", { n: s.families.active7d })]} />
        <Tile icon="🤖" label={t("llmToday")} value={usd(s.llm.today.costUsd)} sub={[t("callsN", { n: s.llm.today.calls })]} />
        <Tile icon="📅" label={t("llm30")} value={usd(s.llm.days30.costUsd)} sub={[t("callsN", { n: s.llm.days30.calls })]} />
      </div>

      <section className="dash-card">
        <h3>🟢 {t("activeUsers")}</h3>
        <div className="active-row">
          {(
            [
              [s.users.active1d, t("activeToday")],
              [s.users.active7d, t("active7")],
              [s.users.active30d, t("active30")],
            ] as const
          ).map(([n, label]) => (
            <div key={label} className="active-cell">
              <b>{n}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="dash-card">
        <h3>📈 {t("activity30")}</h3>
        <Segmented
          value={metric}
          onChange={setMetric}
          options={[
            { value: "activeUsers", label: t("metricActive") },
            { value: "requests", label: t("metricRequests") },
            { value: "transactions", label: t("metricTx") },
            { value: "costUsd", label: t("metricCost") },
          ]}
        />
        <BarChart values={values} labels={labels} format={format} />
      </section>

      <LlmCard s={s} lang={lang} />

      <section className="dash-card">
        <h3>🗄 {t("contentTitle")}</h3>
        <p className="card-text">
          {t("contentLine", { tx: s.content.transactions, r: s.content.receipts, l: s.content.lists, i: s.content.items })}
        </p>
      </section>

      <p className="hint center">
        {t("updatedAt", { time: new Date(s.generatedAt).toLocaleTimeString(lang === "uz" ? "uz-UZ" : "ru-RU", { hour: "2-digit", minute: "2-digit" }) })} ·{" "}
        <button type="button" className="btn-small-ghost" onClick={remote.reload}>
          ↻ {t("aiRefresh")}
        </button>
      </p>
    </>
  );
}

function LlmCard({ s, lang }: { s: AdminStatsDto; lang: Lang }) {
  const totals = s.llm.days30;
  return (
    <section className="dash-card">
      <h3>🤖 {t("byPurpose")}</h3>
      {s.llm.byPurpose30.length === 0 ? (
        <p className="hint">—</p>
      ) : (
        <ul className="plain-list">
          {s.llm.byPurpose30.map((p) => (
            <li key={p.purpose}>
              <span>
                {tryT(`purpose.${p.purpose}`) ?? p.purpose} <span className="muted">· {t("callsN", { n: p.calls })}</span>
              </span>
              <b>{usd(p.costUsd)}</b>
            </li>
          ))}
        </ul>
      )}
      {s.llm.byModel30.length > 0 && (
        <>
          <h3 className="sub-heading">🧠 {t("byModel")}</h3>
          <ul className="plain-list">
            {s.llm.byModel30.map((m) => (
              <li key={m.model}>
                <span>
                  {m.model} <span className="muted">· {t("callsN", { n: m.calls })}</span>
                </span>
                <b>{usd(m.costUsd)}</b>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="hint">
        {t("tokensLine", { in: compact(totals.inputTokens, lang), out: compact(totals.outputTokens, lang), cache: compact(totals.cacheReadTokens, lang) })}
      </p>
    </section>
  );
}

function Families({ lang }: { lang: Lang }) {
  const remote = useRemote("admin:families", () => api.adminFamilies().then((r) => r.families));
  if (!remote.data) return <Loading remote={remote} />;

  const toggle = async (f: AdminFamilyDto, block: boolean) => {
    if (block && !(await confirmDialog(t("confirmBlockFamily", { name: f.name }), t("block")))) return;
    try {
      await api.adminBlockFamily(f.id, block);
      hapticTap();
      toast(t("savedOk"));
      remote.reload();
    } catch (err) {
      toast(errorText(err));
    }
  };

  return (
    <>
      {remote.data.map((f) => {
        const allBlocked = f.members.some((m) => m.blocked);
        return (
          <section key={f.id} className="dash-card">
            <div className="dash-card-head">
              <h3>🏠 {f.name}</h3>
              <span className="muted small">{usd(f.llm30.costUsd)}</span>
            </div>
            <div className="chips member-chips">
              {f.members.map((m) => (
                <span key={m.userId} className={m.blocked ? "tag blocked" : "tag"}>
                  {ROLE_ICON[m.role]} {m.name}
                </span>
              ))}
            </div>
            <div className="row-meta">
              {[t("membersN", { n: f.members.length }), t("txN", { n: f.transactions }), f.lastActivityAt ? t("activeAgo", { when: ago(f.lastActivityAt, lang) }) : t("noActivity")].join(" · ")}
            </div>
            <button type="button" className={allBlocked ? "btn btn-block" : "btn danger btn-block"} onClick={() => void toggle(f, !allBlocked)}>
              {allBlocked ? `✅ ${t("unblockFamily")}` : `⛔ ${t("blockFamily")}`}
            </button>
          </section>
        );
      })}
    </>
  );
}

function matches(u: AdminUserDto, q: string): boolean {
  const needle = q.trim().toLowerCase().replace(/^@/, "");
  if (!needle) return true;
  return [u.name, u.username ?? "", u.familyName ?? "", u.telegramId].some((s) => s.toLowerCase().includes(needle));
}

function Users({ lang }: { lang: Lang }) {
  const remote = useRemote("admin:users", () => api.adminUsers().then((r) => r.users));
  const [q, setQ] = useState("");
  if (!remote.data) return <Loading remote={remote} />;
  const list = remote.data.filter((u) => matches(u, q));

  const toggle = async (u: AdminUserDto) => {
    const block = !u.blocked;
    if (block && !(await confirmDialog(t("confirmBlockUser", { name: u.name }), t("block")))) return;
    try {
      await api.adminBlockUser(u.id, block);
      hapticTap();
      toast(t("savedOk"));
      remote.reload();
    } catch (err) {
      toast(errorText(err));
    }
  };

  return (
    <>
      <input className="input" type="search" placeholder={t("searchPh")} value={q} onChange={(e) => setQ(e.target.value)} />
      {list.length === 0 && <p className="empty">{t("notFound")}</p>}
      {list.length > 0 && (
        <ul className="row-list group">
          {list.map((u) => (
            <li key={u.id} className={u.blocked ? "row user-row blocked" : "row user-row"}>
              <span className="avatar small" aria-hidden="true">
                {u.name.slice(0, 1).toUpperCase()}
              </span>
              <div className="row-body">
                <div className="row-title">
                  {u.name}
                  {u.isDeveloper && <span className="badge">🛠 {t("developerBadge")}</span>}
                  {u.blocked && <span className="badge badge-danger">⛔ {t("blockedBadge")}</span>}
                </div>
                <div className="row-meta">
                  {[
                    u.username ? `@${u.username}` : `id ${u.telegramId}`,
                    u.familyName ? `${u.role ? ROLE_ICON[u.role] : ""} ${u.familyName}` : t("noFamilyL"),
                    u.lastSeenAt ? t("lastSeen", { when: ago(u.lastSeenAt, lang) }) : t("neverSeen"),
                  ].join(" · ")}
                </div>
                <div className="row-meta">
                  {t("requests30", { n: u.botUpdates30 + u.apiRequests30 })} · 🤖 {usd(u.llm30.costUsd)}
                </div>
              </div>
              {!u.isDeveloper && (
                <button type="button" className={u.blocked ? "btn btn-small-plain" : "btn btn-small-plain danger"} onClick={() => void toggle(u)}>
                  {u.blocked ? t("unblock") : t("block")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
